import { AxiosResponse } from 'axios';
import { HomeDisplayBanner } from 'src/admin-entity/interface/home-display/home-display-banner.interface';
import { HomeDisplayCategory } from 'src/admin-entity/interface/home-display/home-display-category.interface';
import { HomeDisplayItem } from 'src/admin-entity/interface/home-display/home-display-item.interface';
import { Page } from 'src/admin-entity/interface/page/page-info.interface';
import { ProductOld } from 'src/admin-entity/interface/product/product-old.interface';
import { BASE_URL } from 'src/config/api';
import client from './client';
import { HomeDisplayTab } from 'src/admin-entity/interface/home-display/home-display-tab.interface';
import { OrderByType } from 'src/admin-entity/enum/home-display/order-by-type.enum';
import { AutoDisplayProjectType } from 'src/admin-entity/enum/home-display/auto-display-project-type.enum';

export const getHomeDisplayCategoryList = (): Promise<AxiosResponse<HomeDisplayCategory[]>> =>
    client.get(`${BASE_URL}/v1/admin/display-category/simple-list`);

export const getHomeDisplayCategoryDetail = (id: number): Promise<AxiosResponse<HomeDisplayCategory>> =>
    client.get(`${BASE_URL}/v1/admin/display-category/${id}`);

export const createHomeDisplayCategory = (
    homeDisplayCategory: HomeDisplayCategory,
): Promise<AxiosResponse<HomeDisplayCategory>> =>
    client.post(`${BASE_URL}/v1/admin/display-category`, homeDisplayCategory);

export const removeHomeDisplayCategory = (id: number): Promise<AxiosResponse<any>> =>
    client.delete(`${BASE_URL}/v1/admin/display-category/${id}`);

export const updateParentHomeDisplayCategory = (
    id: number,
    name: string,
    status: boolean,
    navigationType: string,
): Promise<AxiosResponse<any>> =>
    client.put(`${BASE_URL}/v1/admin/display-category/${id}`, {
        name,
        status,
        navigationType,
    });

export const updateHomeDisplayCategory = (category: HomeDisplayCategory): Promise<AxiosResponse<HomeDisplayCategory>> =>
    client.put(`${BASE_URL}/v1/admin/display-category/${category.id}`, category);

export const getHomeDisplayCategoryItems = (
    id: number,
    page: number,
    take = 50,
): Promise<AxiosResponse<Page<HomeDisplayItem>>> =>
    client.get(`${BASE_URL}/v1/admin/display-category/${id}/display-items?page=${page}&take=${take}`);

export const getHomeDisplayCategoryTabList = (id: number): Promise<AxiosResponse<Page<HomeDisplayTab>>> =>
    client.get(`${BASE_URL}/v1/admin/display-category/${id}/tab-display-category`);

export const updateHomeDisplayCategoryProductIsPin = (
    displayCategoryId: number,
    displayCategoryProductId: number,
    isPin: boolean,
): Promise<AxiosResponse<any>> =>
    client.put(`${BASE_URL}/v1/admin/display-category/${displayCategoryId}/is-pin/${displayCategoryProductId}`, {
        isPin,
    });

export const addProductsInHomeDisplayCategory = (
    homeDisplayCategoryId: number,
    productIds: number[],
): Promise<AxiosResponse<HomeDisplayItem[]>> =>
    client.post(`${BASE_URL}/v1/admin/display-category/${homeDisplayCategoryId}/products`, productIds);

export const addBannersInHomeDisplayCategory = (
    homeDisplayCategoryId: number,
    bannerIds: number[],
): Promise<AxiosResponse<HomeDisplayBanner[]>> =>
    client.post(`${BASE_URL}/v1/admin/display-category/${homeDisplayCategoryId}/banners`, bannerIds);

export const removeProductsInHomeDisplayCategory = (
    homeDisplayCategoryId: number,
    productIds: number[],
): Promise<AxiosResponse<boolean>> =>
    client.put(`${BASE_URL}/v1/admin/display-category/${homeDisplayCategoryId}/del-products`, productIds);

export const removeTabDisplayCategory = (
    homeDisplayCategoryId: number,
    tabIds: number[],
): Promise<AxiosResponse<any>> =>
    client.put(`${BASE_URL}/v1/admin/display-category/${homeDisplayCategoryId}/tab-display-category/del`, tabIds);

export const removeBannersInHomeDisplayCategory = (
    homeDisplayCategoryId: number,
    bannerIds: number[],
): Promise<AxiosResponse<boolean>> =>
    client.put(`${BASE_URL}/v1/admin/display-category/${homeDisplayCategoryId}/del-banners`, bannerIds);

export const changePriorityProductsInHomeDisplayCategory = (
    homeDisplayCategoryId: number,
    priorities: { id: number; priority: number }[],
): Promise<AxiosResponse<{ id: number; priority: number }[]>> =>
    client.put(`${BASE_URL}/v1/admin/display-category/${homeDisplayCategoryId}/priority`, priorities);

export const changePriorityTabList = (
    homeDisplayCategoryId: number,
    priorities: { id: number; priority: number }[],
): Promise<AxiosResponse<{ id: number; priority: number }[]>> =>
    client.put(
        `${BASE_URL}/v1/admin/display-category/${homeDisplayCategoryId}/tab-display-category/priority`,
        priorities,
    );

export const changeDisplayCategoryPriority = (
    priorities: { id: number; priority: number }[],
): Promise<AxiosResponse<{ id: number; priority: number }[]>> =>
    client.put(`${BASE_URL}/v1/admin/display-category/priority`, priorities);

export const searchHomeDisplayCategory = (
    page: number,
    take: number,
    name: string,
    title: string,
    parentName: string,
    status: string,
    onlyDisplayContentsTypeProduct?: boolean,
): Promise<AxiosResponse<Page<HomeDisplayCategory>>> => {
    console.log(onlyDisplayContentsTypeProduct);
    const url = new URL(`${BASE_URL}/v1/admin/display-category/list`);

    if (page) {
        url.searchParams.append('page', `${page}`);
    }
    if (take) {
        url.searchParams.append('take', `${take}`);
    }
    if (name) {
        url.searchParams.append('name', name);
    }
    if (parentName) {
        url.searchParams.append('parentName', parentName);
    }
    if (title) {
        url.searchParams.append('title', title);
    }
    if (status !== 'all') {
        url.searchParams.append('status', status);
    }
    if (onlyDisplayContentsTypeProduct) {
        url.searchParams.append('displayContentsType', 'product');
    }

    return client.get(url.toString());
};

export const addHomeDisplayCategoryImage = (
    homeDisplayCategoryId: number,
    data: FormData,
    setProgress: (value: any) => void,
): Promise<AxiosResponse<any>> =>
    client.post(`${BASE_URL}/v1/admin/display-category/${homeDisplayCategoryId}/image`, data, {
        onUploadProgress: (e: ProgressEvent) => {
            setProgress(Math.floor(e.loaded / e.total) * 100);
        },
    });

export const removeHomeDisplayCategoryImage = (id: number, imageId: number): Promise<AxiosResponse<any>> =>
    client.delete(`${BASE_URL}/v1/admin/display-category/${id}/image/${imageId}`);

export const addTabDisplayCategory = (displayCategoryId: number, ids: number[]): Promise<AxiosResponse<any>> =>
    client.post(`${BASE_URL}/v1/admin/display-category/${displayCategoryId}/tab-display-category`, ids);

export const updateHomeDisplayCategoryTabName = (
    homeDisplayCategoryId: number,
    tabId: number,
    tabName: string,
): Promise<AxiosResponse<any>> =>
    client.put(
        `${BASE_URL}/v1/admin/display-category/${homeDisplayCategoryId}/tab-display-category/${tabId}/tab-name`,
        {
            tabName,
        },
    );

export const addParentHomeDisplayCategory = (
    name: string,
    status: boolean,
    navigationType: string,
): Promise<AxiosResponse<HomeDisplayCategory>> =>
    client.post(`${BASE_URL}/v1/admin/display-category`, {
        name,
        status,
        navigationType,
    });

export const directAddHomeDisplayCategory = (
    id: number,
    orderByType: OrderByType,
    autoDisplayDays: number,
    autoDisplayProjectType: AutoDisplayProjectType,
    categoryId: number,
    distributorId: number,
    productTagGroupId: number,
): Promise<AxiosResponse<any>> => {
    const body = {
        orderByType,
        autoDisplayDays,
        autoDisplayProjectType,
        category: { id: categoryId },
        distributor: { id: distributorId },
        productTagGroup: { id: productTagGroupId },
    };

    if (categoryId === 0) {
        delete body.category;
    }

    if (distributorId === 0) {
        delete body.distributor;
    }

    if (productTagGroupId === 0) {
        delete body.productTagGroup;
    }

    return client.post(`${BASE_URL}/v1/admin/display-category/${id}/tab-display-category/generator`, body);
};
